import { useState, useEffect, useRef } from "react";
import { useLiveMarket, SYMBOL_TO_ID } from "./UseLivemarket";
import { useNavigate } from "react-router-dom";

// ── Constants ─────────────────────────────────────────────────────────────────
const MARKET_SYMBOLS = [
  "ZECUSDT","DOGEUSDT","TRXUSDT","DASHUSDT","XRPUSDT",
  "YFIUSDT","SHIBUSDT","LTCUSDT","BTCUSDT","ADAUSDT",
  "FILUSDT","DOTUSDT","ETHUSDT","LINKUSDT","BCHUSDT",
  "BNBUSDT",
];

// CoinCap icon CDN covers ALL coins including SHIB, YFI, FIL, DOT, LINK
const COIN_LOGO = (baseSymbol) => {
  const symKey = `${baseSymbol.toUpperCase()}USDT`;
  const id = SYMBOL_TO_ID[symKey];

  if (id) {
    return `https://assets.coincap.io/assets/icons/${id}@2x.png`;
  }

  return `https://cryptoicons.org/api/color/${baseSymbol.toLowerCase()}/64`;
};

// ── Formatters ────────────────────────────────────────────────────────────────
const formatPrice = (price) => {
  const n = Number(price);
  if (!n)          return "0";
  if (n < 0.00001) return n.toFixed(8);
  if (n < 1)       return n.toFixed(5);
  if (n > 1000)    return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return n.toFixed(4);
};

const formatVolume = (vol) => {
  const n = Number(vol);
  if (n >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(2) + "K";
  return n.toFixed(2);
};

// ── Sparkline ─────────────────────────────────────────────────────────────────
function Sparkline({ history = [], positive }) {
  const canvasRef = useRef(null);
  const W = 56, H = 24; // compact size matching exchange UI

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || history.length < 2) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr; canvas.height = H * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    // Smooth out the data — take last 20 points max to avoid crazy oscillations
    const data = history.slice(-20);
    const min = Math.min(...data), max = Math.max(...data);
    const range = max - min || min * 0.001 || 1;
    const padX = 1, padY = 3;

    const pts = data.map((v, i) => ({
      x: padX + (i / (data.length - 1)) * (W - padX * 2),
      y: padY + (1 - (v - min) / range) * (H - padY * 2),
    }));

    const color = positive ? "#18d05c": "#ff5252";

    // Subtle gradient fill
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, positive ? "rgba(0,229,255,0.2)" : "rgba(255,82,82,0.2)");
    grad.addColorStop(1, "rgba(0,0,0,0)");

    // Fill path using linear segments (less wavy than bezier for small data sets)
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) {
      ctx.lineTo(pts[i].x, pts[i].y);
    }
    ctx.lineTo(pts[pts.length - 1].x, H);
    ctx.lineTo(pts[0].x, H);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    // Crisp thin line
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) {
      ctx.lineTo(pts[i].x, pts[i].y);
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.lineJoin = "round";
    ctx.stroke();
  }, [history, positive]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: W, height: H, display: "block" }}
    />
  );
}

// ── Coin avatar with fallback ─────────────────────────────────────────────────
function CoinAvatar({ symbol }) {
  const [failed, setFailed] = useState(false);

  // Color based on symbol for consistent letter avatars
  const colors = [
    "#0e7490","#065f46","#7c3aed","#b45309","#be123c",
    "#1d4ed8","#15803d","#c2410c","#6d28d9","#0369a1",
  ];
  const colorIndex = symbol.charCodeAt(0) % colors.length;
  const bgColor = colors[colorIndex];

  if (failed) {
    return (
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border border-cyan-900/30 text-white font-bold text-sm"
        style={{ backgroundColor: bgColor }}
      >
        {symbol[0]}
      </div>
    );
  }

  return (
    <img
      src={COIN_LOGO(symbol)}
      alt={symbol}
      className="w-9 h-9 rounded-full bg-[#1a2030] border border-cyan-900/30 object-contain flex-shrink-0"
      onError={() => setFailed(true)}
    />
  );
}

// ── Coin row ──────────────────────────────────────────────────────────────────
function CoinRow({ coin, tab }) {
  const change   = Number(coin.priceChangePercent ?? 0);
  const positive = change >= 0;
  const baseSymbol = coin.symbol?.includes("/")
    ? coin.symbol.split("/")[0]
    : coin.symbol?.replace("USDT", "") ?? "?";

  const [flash, setFlash] = useState(null);
  const prevRef = useRef(null);
  const navigate =useNavigate();
  useEffect(() => {
    if (!coin.lastPrice) return;
    if (prevRef.current && coin.lastPrice !== prevRef.current) {
      setFlash(Number(coin.lastPrice) > Number(prevRef.current) ? "up" : "down");
      const t = setTimeout(() => setFlash(null), 500);
      prevRef.current = coin.lastPrice;
      return () => clearTimeout(t);
    }
    prevRef.current = coin.lastPrice;
  }, [coin.lastPrice]);

  const priceClass =
    flash === "up"   ? "text-green-400" :
    flash === "down" ? "text-red-400"  : "text-gray-400";

  return (
    <div
        onClick={() => navigate(`/binary/${coin.symbol}`)}
        className="flex items-center justify-between py-3 border-b border-white/5 last:border-0"      // style={{ gridTemplateColumns: "0.8fr 64px 78px" }}
    >
      {/* Col 1 */}
      <div className="flex items-center gap-2.5"
      >
        <CoinAvatar symbol={baseSymbol} />
        <div>
          <p className="text-white font-bold text-[13px] leading-tight">
            {baseSymbol}
            <span className="text-gray-600 text-[10px] font-normal ml-0.5">/ USDT</span>
          </p>
          <p className={`text-[11px] mt-0.5 tabular-nums transition-colors duration-200 ${priceClass}`}>
            {formatPrice(coin.lastPrice || "0")}
          </p>
        </div>
      </div>

      <div
        style={{
          fontSize: "13px",
          fontWeight: "bold",
          lineHeight: 1.1,
          // display:"flex",
          justifyContent:"center",
          color:
            flash === "up"
              ? "#16C784"
              : flash === "down"
              ? "#ff5252"
              : "#6b7280",
          
        }}
        className="hidden min-[350px]:flex"
      >
        {coin?.lastPrice
          ? Number(coin.lastPrice).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 5,
            })
          : "—"}
      </div>

      {/* Col 2 — sparkline */}
      <div className="flex justify-center items-center">
        {tab !== 2 ? (
          <Sparkline history={coin.history || []} positive={positive} />
        ) : (
          <span className="text-gray-500 text-[11px] tabular-nums">
            {formatVolume(coin.quoteVolume || "0")}
          </span>
        )}
      </div>

      {/* Col 3 — badge */}
      <div className="flex justify-end">
        {tab !== 2 ? (
          <span className={`text-[12px] font-bold tabular-nums px-2.5 py-1.5 rounded-lg border whitespace-nowrap ${
            positive
              ? "bg-green-400/10 text-green-400 border-green-400/30"
              : "bg-red-500/10 text-red-400 border-red-500/30"
          }`}>
            {positive ? "+" : ""}{change.toFixed(2)}%
          </span>
        ) : (
          <span className="text-gray-200 font-semibold text-[12px] tabular-nums">
            {formatVolume(coin.quoteVolume || "0")}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ mode }) {
  const cfg = {
    ws:         { color: "text-cyan-400",  dot: "bg-cyan-400",  label: "LIVE",        glow: "0 0 6px #00e5ff" },
    polling:    { color: "text-green-400", dot: "bg-green-400", label: "LIVE ~2s",    glow: "0 0 6px #4ade80" },
    connecting: { color: "text-amber-400", dot: "bg-amber-400", label: "Connecting…", glow: "none"            },
  }[mode] ?? { color: "text-amber-400", dot: "bg-amber-400", label: "…", glow: "none" };

  return (
    <div className="flex items-center gap-1.5 mb-2">
      <span
        className={`w-1.5 h-1.5 rounded-full animate-pulse ${cfg.dot}`}
        style={{ boxShadow: cfg.glow }}
      />
      <span className={`text-[10px] font-bold tracking-widest ${cfg.color}`}>
        {cfg.label}
      </span>
    </div>
  );
}

export default function MarketTable() {
  const [tab, setTab] = useState(0);
  const { coins, mode } = useLiveMarket();

  const coinArray = MARKET_SYMBOLS
    .map((s) => coins[s])
    .filter((c) => c && c.lastPrice);

  const TABS = ["Change", "Top Losers", "24h turnover"];

  const displayData =
    tab === 0
      ? [...coinArray].sort(
          (a, b) => Number(b.priceChangePercent) - Number(a.priceChangePercent)
        )
      : tab === 1
      ? [...coinArray].sort(
          (a, b) => Number(a.priceChangePercent) - Number(b.priceChangePercent)
        )
      : [...coinArray].sort(
          (a, b) => Number(b.quoteVolume) - Number(a.quoteVolume)
        );

  return (
    <div>
      {/* Tabs */}
      <div className="flex items-center justify-center gap-6 mb-3">
        {TABS.map((label, i) => (
          <button
            key={label}
            onClick={() => setTab(i)}
            className={`relative text-[13px] font-medium pb-1 transition-colors ${
              tab === i ? "text-cyan-400" : "text-gray-500 hover:text-gray-300"
            }`}
          >
            {label}
            {tab === i && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-cyan-400" />
            )}
          </button>
        ))}
      </div>

      {/* Status */}
      <StatusBadge mode={mode} />

      {/* Empty state */}
      {displayData.length === 0 && (
        <div className="py-6 text-center text-gray-500 text-sm">
          Loading market data…
        </div>
      )}

      {/* Rows */}
      {displayData.map((coin) => (
        <CoinRow key={coin.symbol} coin={coin} tab={tab} />
      ))}
    </div>
  );
}